import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Link2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export function PayActions({ eventId, payeeType, refId, name, email, agreedCents = 0, paidCents = 0, onDone }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const remaining = Math.max(0, (agreedCents || 0) - (paidCents || 0));
  const [amount, setAmount] = useState(remaining >= 100 ? (remaining / 100).toFixed(2) : '');
  const [note, setNote] = useState('');
  const target = { payee_type: payeeType, ref_id: refId };

  const sendLink = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/payees/setup-link`, { ...target, send_email: true }, auth());
      if (r.data.emailed) toast.success(`Bank-setup link emailed to ${name}`);
      else { await navigator.clipboard.writeText(r.data.url).catch(() => {}); toast.success('Setup link copied — send it to them'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create setup link'); }
    finally { setBusy(false); }
  };

  const pay = async () => {
    const cents = Math.round(parseFloat(amount || '0') * 100);
    if (!cents || cents < 100) return toast.error('Minimum payment is $1.00');
    setBusy(true);
    try {
      await axios.post(`${API}/events/${eventId}/pay`, { ...target, amount_cents: cents, note }, auth());
      toast.success(`${usd(cents)} sent to ${name} — lands in their bank in 1-2 business days`);
      setOpen(false); setNote('');
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
    finally { setBusy(false); }
  };

  return (
    <span className="inline-flex gap-1.5">
      <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy || !email}
        title={email ? 'Email them a one-time bank-setup link' : 'Add an email first'}
        onClick={sendLink} data-testid={`payact-link-${refId}`}>
        <Link2 className="h-3 w-3 mr-1" /> Bank link
      </Button>
      <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || !email}
        title={email ? 'Pay from your Eventism wallet' : 'Add an email first'}
        onClick={() => setOpen(true)} data-testid={`payact-pay-${refId}`}>
        <Send className="h-3 w-3 mr-1" /> Pay
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Pay {name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">Comes out of your Eventism wallet and lands in their bank in 1–2 business days.{agreedCents > 0 && ` Agreed: ${usd(agreedCents)}${paidCents > 0 ? ` · paid so far: ${usd(paidCents)}` : ''}.`}</p>
            <div>
              <label className="text-xs font-medium">Amount ($)</label>
              <Input type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="150.00" data-testid="payact-amount" />
            </div>
            <div>
              <label className="text-xs font-medium">Note (they'll see it)</label>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Thanks for the event!" data-testid="payact-note" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={pay} data-testid="payact-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Send ${amount ? `$${parseFloat(amount).toFixed(2)}` : 'payment'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
}
